import type { HistoryEntry } from "../types/portfolio";

/**
 * Returns updated history array if value changed since last snapshot, else null.
 * Dedup: skip if value unchanged from latest entry.
 * Same-day update: replace today's entry rather than append.
 */
export function maybeAppendSnapshot(
  history: HistoryEntry[] | undefined,
  newValue: number | undefined,
): HistoryEntry[] | null {
  if (newValue === undefined || newValue === null || newValue <= 0) return null;
  const today = new Date().toISOString().slice(0, 10);
  const existing = history ?? [];
  const sorted = [...existing].sort((a, b) => a.date.localeCompare(b.date));
  const latest = sorted[sorted.length - 1];

  if (latest && latest.value === newValue) return null;

  const filtered = sorted.filter((e) => e.date !== today);
  return [...filtered, { id: crypto.randomUUID(), date: today, value: newValue }];
}

/**
 * Compound annual growth rate between earliest and latest entries.
 * Returns null if fewer than 2 entries or insufficient time span (< 3 months).
 */
export function calculateCAGR(history: HistoryEntry[] | undefined): number | null {
  if (!history || history.length < 2) return null;
  const sorted = [...history].sort((a, b) => a.date.localeCompare(b.date));
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const years = (new Date(last.date).getTime() - new Date(first.date).getTime()) / (365.25 * 24 * 60 * 60 * 1000);
  if (years < 0.25) return null;
  if (first.value <= 0) return null;
  return Math.pow(last.value / first.value, 1 / years) - 1;
}

/**
 * Year-over-year percentage change for the most recent year.
 * Finds the entry closest to ~12 months before the latest entry.
 * Returns null if insufficient data.
 */
export function calculateYoYGrowth(history: HistoryEntry[] | undefined): number | null {
  if (!history || history.length < 2) return null;
  const sorted = [...history].sort((a, b) => a.date.localeCompare(b.date));
  const latest = sorted[sorted.length - 1];
  const oneYearAgo = new Date(latest.date);
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
  const target = oneYearAgo.toISOString().slice(0, 10);
  let closest = sorted[0];
  let closestDiff = Math.abs(new Date(closest.date).getTime() - new Date(target).getTime());
  for (const e of sorted) {
    if (e === latest) continue;
    const diff = Math.abs(new Date(e.date).getTime() - new Date(target).getTime());
    if (diff < closestDiff) { closest = e; closestDiff = diff; }
  }
  if (closest === latest || closest.value <= 0) return null;
  return (latest.value - closest.value) / closest.value;
}

/**
 * Total growth percentage from earliest to latest entry.
 * Returns null if fewer than 2 entries.
 */
export function calculateTotalGrowth(history: HistoryEntry[] | undefined): number | null {
  if (!history || history.length < 2) return null;
  const sorted = [...history].sort((a, b) => a.date.localeCompare(b.date));
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  if (first.value <= 0) return null;
  return (last.value - first.value) / first.value;
}
